import React, { useState, useContext } from 'react';
import { SimplifiedChampion, ResourceType } from '../../../../../gameTypes'; 
import './StatsDisplay.css';
import SocketContext from '../../../../../../NewWebSocket/components/WebSocketAdapter';

interface ChampionStatsProps {
  champion: SimplifiedChampion;
  onShowExpanded?: () => void;
  showingExpanded?: boolean;
}


//Todo: Add energy icon

const ChampionStatsDisplay: React.FC<ChampionStatsProps> = ({ champion, onShowExpanded, showingExpanded }) => {
  const stats = champion.stats.totalStats;
  const socket = useContext(SocketContext);

  // Commented out for now - might be needed later
  // const [isModalOpen, setIsModalOpen] = useState(false);
  // const [selectedStat, setSelectedStat] = useState<string>('attackDamage');
  // const [statValue, setStatValue] = useState<number>(10);

  // -- 1) figure out how to display resource based on champion.stats.primaryResource
  let displayedCurrentResource = 0;
  let displayedMaxResource = 0;
  let displayedResourceRegen = 0;

  // If the championStats includes primaryResource, we read it
  if (champion.stats.primaryResource) {
    const { type, current, max } = champion.stats.primaryResource;
    
    switch (type) {
      case "Mana":
        displayedCurrentResource = Math.round(current);
        displayedMaxResource = Math.round(max);
        // for mana, we can read champion.stats.totalStats.manaRegen 
        displayedResourceRegen = Math.round(stats.manaRegen ?? 0);
        break;
      case "Energy":
        displayedCurrentResource = Math.round(current);
        displayedMaxResource = Math.round(max);
        // If you also store an "energyRegen" in totalStats or somewhere, do that:
        // displayedResourceRegen = champion.stats.totalStats.energyRegen ?? 0;
        break;
      case "Fury":
      case "Flow":
      case "None":
      default:
        // Could display them if you want, or 0
        // For demonstration, just store current for a Fury bar or something
        displayedCurrentResource = Math.round(current);
        displayedMaxResource = Math.round(max);
        displayedResourceRegen = 0; 
        break;
    }
  } 

  // 2) The rest of your code is unchanged:
  // Commented out for now - might be needed later
  // const handleAddStatClick = () => {
  //   setIsModalOpen(true);
  // };
  // const handleStatChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
  //   setSelectedStat(e.target.value);
  // };
  // const handleValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  //   setStatValue(Number(e.target.value));
  // };
  // const handleSubmit = () => {
  //   if (socket) {
  //     socket.emit('add-stat-modifier', {
  //       championId: champion.id,
  //       stat: selectedStat,
  //       value: statValue,
  //     });
  //   }
  //   setIsModalOpen(false);
  // };
  // const handleRemoveStatModifier = (stat: string) => {
  //   if (socket) {
  //     socket.emit('remove-stat-modifier', {
  //       championId: champion.id,
  //       stat: stat,
  //     });
  //   }
  // };

  return (
    <div className="stats-display-wrapper">
      <div className="stats-display">
        <div className="stat-container">

          {/* Left Column */}
          <div className="stat-column">
            {/* Attack Damage */}
            <div className="stat-item">
              <div className="stat-icon attack-damage-icon"></div>
              <div className="stat-value">{Math.round(stats.attackDamage ?? 0)}</div>
            </div>
            {/* Armor */}
            <div className="stat-item">
              <div className="stat-icon armor-icon"></div>
              <div className="stat-value">{Math.round(stats.armor ?? 0)}</div>
            </div>
            {/* Attack Speed */}
            <div className="stat-item">
              <div className="stat-icon attack-speed-icon"></div>
              <div className="stat-value">{(stats.attackSpeed ?? 0).toFixed(2)}</div>
            </div>
            {/* Crit Chance */}
            <div className="stat-item">
              <div className="stat-icon crit-chance-icon"></div>
              <div className="stat-value">{Math.round(stats.criticalStrikeChance ?? 0)}</div>
            </div>

            <div className="divider-stats"></div>

            {/* Health */}
            <div className="stat-item">
              <div className="stat-icon health-icon"></div>
              <div className="stat-value">
                {Math.round(stats.currentHealth ?? 0)} / {Math.round(stats.health ?? 0)}
              </div>
            </div>
            {/* Health Regen */}
            <div className="stat-item">
              <div className="stat-icon health-regen-icon"></div>
              <div className="stat-value">
                {Math.round(stats.healthRegen ?? 0)}
              </div>
            </div>
            {/* Armor Pen */}
            <div className="stat-item">
              <div className="stat-icon armor-penetration-icon"></div>
              <div className="stat-value-group">
                <div className="stat-value">{Math.round(stats.lethality ?? 0)}</div>
                <div className="vertical-divider"></div>
                <div className="stat-value">
                  {Math.round(stats.armorPenetrationPercent ?? 0)}%
                </div>
              </div>
            </div>
            {/* Lifesteal */}
            <div className="stat-item">
              <div className="stat-icon life-steal-icon"></div>
              <div className="stat-value">{Math.round(stats.lifeSteal ?? 0)}</div>
            </div>
            {/* Attack Range */}
            <div className="stat-item">
              <div className="stat-icon attack-range-icon"></div>
              <div className="stat-value">{Math.round(stats.attackRange ?? 0)}</div>
            </div>
            {/* Heal & Shield Power */}
            <div className="stat-item">
              <div className="stat-icon heal-shield-power-icon"></div>
              <div className="stat-value">{Math.round(stats.healShieldPower ?? 0)}%</div>
            </div>
          </div>

          {/* Right Column */}
          <div className="stat-column">
            {/* Ability Power */}
            <div className="stat-item">
              <div className="stat-icon ability-power-icon"></div>
              <div className="stat-value">{Math.round(stats.abilityPower ?? 0)}</div>
            </div>
            {/* Magic Resist */}
            <div className="stat-item">
              <div className="stat-icon magic-resist-icon"></div>
              <div className="stat-value">{Math.round(stats.magicResistance ?? 0)}</div>
            </div>
            {/* Ability Haste */}
            <div className="stat-item">
              <div className="stat-icon ability-haste-icon"></div>
              <div className="stat-value">{Math.round(stats.abilityHaste ?? 0)}</div>
            </div>
            {/* Movement Speed */}
            <div className="stat-item">
              <div className="stat-icon movement-speed-icon"></div>
              <div className="stat-value">{Math.round(stats.movementSpeed ?? 0)}</div>
            </div>

            <div className="divider-stats"></div>

            {/* Resource */}
            <div className="stat-item">
              <div className="stat-icon resource-icon"></div>
              <div className="stat-value">
                {displayedCurrentResource} / {displayedMaxResource}
              </div>
            </div>
            {/* Resource Regen */}
            <div className="stat-item">
              <div className="stat-icon resource-regen-icon"></div>
              <div className="stat-value">
                {displayedResourceRegen}
              </div>
            </div>
            
            {/* Magic Pen */}
            <div className="stat-item">
              <div className="stat-icon magic-penetration-icon"></div>
              <div className="stat-value-group">
                <div className="stat-value">{Math.round(stats.magicPenetration ?? 0)}</div>
                <div className="vertical-divider"></div>
                <div className="stat-value">
                  {Math.round(stats.magicPenetrationPercent ?? 0)}%
                </div>
              </div>
            </div>
            {/* Omnivamp */}
            <div className="stat-item">
              <div className="stat-icon omnivamp-icon"></div>
              <div className="stat-value">{Math.round(stats.omnivamp ?? 0)}</div>
            </div>
            {/* Tenacity */}
            <div className="stat-item">
              <div className="stat-icon tenacity-icon"></div>
              <div className="stat-value">{Math.round(stats.tenacity ?? 0)}%</div>
            </div>
          </div>
        </div>
      </div>

      {/* Commented out for now - might be needed later */}
      {/* <button onClick={handleAddStatClick}>Add Stat</button> */}

      {/* Button to show expanded stats */}
      {onShowExpanded && (
        <button onClick={onShowExpanded} className="show-expanded-stats-button">
          {showingExpanded ? 'Hide Detailed Stats' : 'Show Detailed Stats'}
        </button>
      )}

      {/* Commented out modal for adding stat */}
      {/* {isModalOpen && (
        <div className="modal">
          <h3>Add Stat Modifier</h3>
          <label>
            Select Stat:
            <select value={selectedStat} onChange={handleStatChange}>
              {Object.keys(stats).map((statKey) => (
                <option key={statKey} value={statKey}>
                  {statKey}
                </option>
              ))}
            </select>
          </label>
          <label>
            Value:
            <input
              type="number"
              value={statValue}
              onChange={handleValueChange}
            />
          </label>
          <button onClick={handleSubmit}>Submit</button>
          <button onClick={() => setIsModalOpen(false)}>Cancel</button>
        </div>
      )} */}
    </div>
  );
};

export default ChampionStatsDisplay;
